import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { RootState } from "../store"

export const ovationApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_APP_API_URL}/${import.meta.env.VITE_APP_API_VERSION}`,
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState
      const authToken = state.auth.masquerade_access_token || state.auth.api_access_token
      const tokenType = state.auth.token_type
      headers.set("Content-Type", "application/json")
      headers.set("Accept", "application/json")
      if (authToken) {
        headers.set("authorization", `${tokenType} ${authToken}`)
      }
      return headers
    },
  }),

  reducerPath: "ovationApi",
  tagTypes: ["Quiz", "Answer"],
  endpoints: (builder) => ({
    ovationWords: builder.mutation<API.Response.OvationWordResponse, API.Payload.OvationWordPayload>({
      query: (payload) => ({
        url: "/ovation/words",
        method: "POST",
        body: payload,
      }),
    }),

    ovationRecord: builder.mutation<API.Response.OvationRecordResponse, API.Payload.OvationRecordPayload>({
      query: (payload) => ({
        url: "/ovation/records",
        method: "POST",
        body: payload,
      }),
    }),

    ovationTrend: builder.mutation<API.Response.OvationTrendResponse, API.Payload.OvationTrendPayload>({
      query: (payload) => ({
        url: "/ovation/trend",
        method: "POST",
        body: payload,
      }),
    }),

    //ovation negative
    ovationNegativeWords: builder.mutation<API.Response.OvationWordResponse, API.Payload.OvationWordPayload>({
      query: (payload) => ({
        url: "/ovation/negative/words",
        method: "POST",
        body: payload,
      }),
    }),

    ovationNegativeRecord: builder.mutation<API.Response.OvationRecordResponse, API.Payload.OvationRecordPayload>({
      query: (payload) => ({
        url: "/ovation/negative/records",
        method: "POST",
        body: payload,
      }),
    }),

    ovationNegativeTrend: builder.mutation<API.Response.OvationTrendResponse, API.Payload.OvationTrendPayload>({
      query: (payload) => ({
        url: "/ovation/negative/trend",
        method: "POST",
        body: payload,
      }),
    }),

    ovationLeaderboard: builder.mutation<
      API.Response.OvationLeaderboardResponse,
      API.Payload.OvationLeaderboardPayload
    >({
      query: (payload) => ({
        url: "/ovation/rank/team",
        method: "POST",
        body: payload,
      }),
    }),

    ovationGlobalLeaderboard: builder.mutation<
      API.Response.OvationLeaderboardResponse,
      API.Payload.OvationLeaderboardPayload
    >({
      query: (payload) => ({
        url: "/ovation/rank",
        method: "POST",
        body: payload,
      }),
    }),

    ovationNotification: builder.mutation<
      API.Response.OvationNotificationResponse,
      API.Payload.OvationNotificationPayload
    >({
      query: (payload) => ({
        url: "/ovation/me",
        method: "POST",
        body: payload,
      }),
    }),

    //Avatar
    userAvatar: builder.query<API.Response.BeYouAvatarResponse, void>({
      query: () => "/ovation/avatar",
    }),

    getAvatarItems: builder.query<API.Response.AvatarItemsResponse, string>({
      query: (item) => `ovation/avatar/items/${item}`,
    }),

    avatarItemUnlock: builder.mutation<
      API.Response.BeYouAvatarItemUnlockResponse,
      API.Payload.BeYouAvatarItemUnlockPayload
    >({
      query: (payload) => ({
        url: "/ovation/avatar/unlock",
        method: "POST",
        body: payload,
      }),
    }),

    saveAvatar: builder.mutation<API.Response.BeYouAvatarSaveResponse, API.Payload.BeYouAvatarSavePayload>({
      query: (payload) => ({
        url: "/ovation/avatar/save",
        method: "POST",
        body: payload,
      }),
    }),
    saveAvatarImage: builder.mutation<
      API.Response.BeYouAvatarImageSaveResponse,
      API.Payload.BeYouAvatarImageSavePayload
    >({
      query: (payload) => ({
        url: "/ovation/avatar/image/save",
        method: "POST",
        body: payload,
      }),
    }),

    ovationTeamOverview: builder.mutation<
      API.Response.OvationTeamOverviewResponse,
      API.Payload.OvationTeamOverviewPayload
    >({
      query: (payload) => ({
        url: "/ovation/team",
        method: "POST",
        body: payload,
      }),
    }),
  }),
})

export const {
  useAvatarItemUnlockMutation,
  useGetAvatarItemsQuery,
  useLazyGetAvatarItemsQuery,
  useLazyUserAvatarQuery,
  useOvationGlobalLeaderboardMutation,
  useOvationLeaderboardMutation,
  useOvationNegativeRecordMutation,
  useOvationNegativeTrendMutation,
  useOvationNegativeWordsMutation,
  useOvationNotificationMutation,
  useOvationRecordMutation,
  useOvationTrendMutation,
  useOvationWordsMutation,
  useSaveAvatarImageMutation,
  useSaveAvatarMutation,
  useUserAvatarQuery,
} = ovationApi
